import { createContext, useContext } from 'react';
import localForage from 'localforage';
import { useCart } from '@elevator/api-hooks/order/use-cart';
import { CartQuery } from '@elevator/api-hooks/types/schema';

type SubmitCartMeta = {
  cartId?: string;
  step: number;
  timestamp: number;
};

const STORAGE_KEY = '@elevator/submit-cart';
const CART_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Deletes cart metadata from local storage
 */
export const deleteCartMeta = () => {
  return localForage.removeItem(STORAGE_KEY);
};

/**
 * Retrieves cart metadata from local storage.
 * Automatically cleans up stale data older than 24 hours.
 * Metadata without a timestamp is treated as expired for safety.
 * @returns Cart metadata or null if not found or expired
 */
export const getCartMeta = async () => {
  const meta = await localForage.getItem<SubmitCartMeta>(STORAGE_KEY);

  if (!meta) {
    return null;
  }

  // If metadata exists but has no timestamp, treat as expired (migration from old format)
  if (!meta.timestamp) {
    await deleteCartMeta();
    return null;
  }

  // Auto-cleanup stale data (older than 24 hours)
  const age = Date.now() - meta.timestamp;
  if (age > CART_EXPIRY_MS) {
    await deleteCartMeta();
    return null;
  }

  return meta;
};

/**
 * Saves cart metadata to local storage with current timestamp
 * @param cartId - The cart/order ID from the backend
 * @param step - The current step in the submission flow
 */
export const setCartMeta = (cartId: string, step: number) => {
  return localForage.setItem<SubmitCartMeta>(STORAGE_KEY, {
    cartId,
    step,
    timestamp: Date.now(),
  });
};

/**
 * Updates only the step number in cart metadata, preserving cartId and updating timestamp
 * @param step - The new step number
 */
export const setCartStep = async (step: number) => {
  const cartMeta = await getCartMeta();

  if (cartMeta && cartMeta.cartId) {
    await setCartMeta(cartMeta.cartId, step);
  }
};

/**
 * Explicitly checks and cleans up stale cart data
 * @returns true if cleanup occurred, false otherwise
 */
export const cleanupStaleCart = async (): Promise<boolean> => {
  const meta = await localForage.getItem<SubmitCartMeta>(STORAGE_KEY);
  if (meta && meta.timestamp) {
    const age = Date.now() - meta.timestamp;
    if (age > CART_EXPIRY_MS) {
      await deleteCartMeta();
      return true;
    }
  }
  return false;
};

export type Cart = CartQuery['cart'];

export const CartContext = createContext<
  (ReturnType<typeof useCart> & { step: number }) | null
>(null);

export const CartProvider = CartContext.Provider;

/**
 * Hook to access cart context.
 * @throws Error if used outside CartProvider
 * @returns Cart context with cart data and step number
 */
export const useCartContext = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCartContext must be used within CartProvider');
  }
  return context;
};
